<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'rental',
    path: '/rentals/:id',
    component: 'Rental',
    params: [
      {
        name: 'id',
        prop: 'rentalId',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Rental',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    rentalId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const tabManager = ref(null);
  const item = ref(null);
  const messages = ref({
    delete: $fn.value.pgettext('Rental|Header', 'Delete rental'),
  });
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_8564662037462133_parentTo = computed(() => ({
    name: 'app.rentals',
  }));

  const $data = computed(() => ({
    tabManager: tabManager.value,
    item: item.value,
    messages: messages.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8564662037462133_parentTo: b_8564662037462133_parentTo.value,
  }));

  const $$load_tabManager = () => {
    return new $app.value.utils.LazyTabManager();
  };
  const $$load_item = () => {
    return $fn.value.fetch('read_rental', {
      asResource: true,
      parameters: { id: props.rentalId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'tabManager' },
      { name: 'item' },
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8564662037462133_parentTo', nodeId: '8564662037462133' },
    ],
    loaders: { tabManager: $$load_tabManager, item: $$load_item },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      tabManager,
      item,
      messages,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8564662037462133_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [item.value.snapshot('name'), $fn.value.gettext('Rental')],
        icon: $config.value.rental.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_8basrj374_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_8basrj374 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_8basrj374_getActionArgs,
    });
  const inst_evh_7315092382398562_close_xzdh2mu7u =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_8basrj374,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_xzdh2mu7u],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_q8qfcaqck =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ho9psooqk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_q8qfcaqck,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ho9psooqk],
    event: 'reload',
  });
  const act_4615685607825208_reloadComponentData_um53koh9t =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_u0v0jgm3a =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_um53koh9t,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_u0v0jgm3a],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_closeComponent_fz79ulikq =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2095089031469157_success_wbr09p5a2 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_closeComponent_fz79ulikq,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_2095089031469157_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_success_wbr09p5a2],
    event: 'success',
  });
  const act_2679161326024595_reloadComponentData_m05qh0cde =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2679161326024595_reload_6c1nvwwwk =
    new actionTypes.EventHandlerImpl({
      action: act_2679161326024595_reloadComponentData_m05qh0cde,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2679161326024595_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2679161326024595_reload_6c1nvwwwk],
    event: 'reload',
  });
  const act_7987652045662005_reloadComponentData_n6j5qf7wh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7987652045662005_reload_elhzpeln1 =
    new actionTypes.EventHandlerImpl({
      action: act_7987652045662005_reloadComponentData_n6j5qf7wh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7987652045662005_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7987652045662005_reload_elhzpeln1],
    event: 'reload',
  });
  const act_6490121676898844_reloadComponentData_zvnsafqsy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6490121676898844_reload_g1ak6n1xj =
    new actionTypes.EventHandlerImpl({
      action: act_6490121676898844_reloadComponentData_zvnsafqsy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6490121676898844_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6490121676898844_reload_g1ak6n1xj],
    event: 'reload',
  });
  const act_8697745105058001_reloadComponentData_oc4ll87z2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8697745105058001_reload_yp86xezho =
    new actionTypes.EventHandlerImpl({
      action: act_8697745105058001_reloadComponentData_oc4ll87z2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_8697745105058001_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8697745105058001_reload_yp86xezho],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_xzdh2mu7u,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_ho9psooqk,
    inst_evh_7315092382398562_reload,
    inst_evh_4615685607825208_clickPreventStop_u0v0jgm3a,
    reload: inst_evh_4615685607825208_clickPreventStop_u0v0jgm3a,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_success_wbr09p5a2,
    inst_evh_2095089031469157_success,
    inst_evh_2679161326024595_reload_6c1nvwwwk,
    inst_evh_2679161326024595_reload,
    inst_evh_7987652045662005_reload_elhzpeln1,
    inst_evh_7987652045662005_reload,
    inst_evh_6490121676898844_reload_g1ak6n1xj,
    inst_evh_6490121676898844_reload,
    inst_evh_8697745105058001_reload_yp86xezho,
    inst_evh_8697745105058001_reload,
  };

  const evh_7315092382398562_close_xzdh2mu7u = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_xzdh2mu7u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_ho9psooqk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ho9psooqk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_u0v0jgm3a = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_u0v0jgm3a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_wbr09p5a2 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_wbr09p5a2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload_6c1nvwwwk = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload_6c1nvwwwk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload_elhzpeln1 = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload_elhzpeln1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6490121676898844_reload_g1ak6n1xj = async (event, scope) => {
    return await actions.inst_evh_6490121676898844_reload_g1ak6n1xj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6490121676898844_reload = async (event, scope) => {
    return await actions.inst_evh_6490121676898844_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_reload_yp86xezho = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_reload_yp86xezho.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_reload = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Rental"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="page--v2 d-flex flex-column" v-if="loaded">
      <MaterialTabs
        :tabManager="tabManager"
        :title="item.snapshot('name')"
        :parentText="gettext('Rentals')"
        :parentTo="b_8564662037462133_parentTo"
        class="h-100"
      >
        <template #actions>
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            size="sm"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BtnDeleteResource
            :resource="item"
            :dialogHeader="messages.delete"
            @success="evh_2095089031469157_success($event, {})"
          >
          </BtnDeleteResource>
        </template>
        <BTab :title="gettext('General')" class="container-lg float-start">
          <ItpRow>
            <ItpCol :w="6">
              <UcRentalGeneralPanel
                :rental="item"
                :reload="lastLoaded"
                class="pb-5"
                @reload="evh_2679161326024595_reload($event, {})"
              >
              </UcRentalGeneralPanel>
              <UcRentalFormContracts
                :rental="item"
                :reload="lastLoaded"
                class="pb-5"
                v-if="item"
                @reload="evh_7987652045662005_reload($event, {})"
              >
              </UcRentalFormContracts>
              <UcRentalFormProperties
                :rentalId="item.id"
                :reload="lastLoaded"
                class="pb-5"
                v-if="item"
                @reload="evh_6490121676898844_reload($event, {})"
              >
              </UcRentalFormProperties>
            </ItpCol>
            <ItpCol :w="6">
              <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-5">
              </UcCommentsList>
              <UcAttachmentsList
                :parent="item"
                :reload="lastLoaded"
                class="pb-5"
              >
              </UcAttachmentsList>
            </ItpCol>
          </ItpRow>
        </BTab>
        <BTab :title="gettext('Services')" class="container-fluid h-100">
          <UcRentalServices
            :rental="item"
            :reload="lastLoaded"
            v-if="tabManager.visited(1)"
            @reload="evh_8697745105058001_reload($event, {})"
          >
          </UcRentalServices>
        </BTab>
        <BTab :title="gettext('Fixed Assets')" class="container-fluid h-100">
          <UcRentalFormFixedAssets
            :rentalId="item.id"
            :editable="!item.locked"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(2)"
          >
          </UcRentalFormFixedAssets>
        </BTab>
        <BTab
          :title="pgettext('Rental', 'Billing list')"
          class="container-fluid h-100"
        >
          <UcBillingList
            :rentalId="item.id"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(3)"
          >
          </UcBillingList>
        </BTab>
        <BTab
          :title="pgettext('Rental', 'Invoices')"
          class="container-fluid h-100"
        >
          <InvoiceLinesView
            :rentalId="item.id"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(4)"
          >
          </InvoiceLinesView>
        </BTab>
      </MaterialTabs>
    </div>
  </ItpPage>
</template>
